import React, { Dispatch, SetStateAction, useEffect } from "react";
import { CoObjectContext } from "../ObjectPanelElaborationWrapper";
import { useAppSelector } from "../../../redux/hooks/hooks";
import { selectCpmUsername } from "../../../redux/feature/settings/configurations-slice";
import LoadingManager from "../../../common/loading/LoadingManager";
import { SAVE_COOBJECT_NOTIFICATION } from "../../../utils/constants/notification";
import { fetchExecutedFormAndEditAsync, fetchExecutedFormAndInsertAsync } from "../../../redux/feature/cpm/cpm-slice";
import NotificationManager from "../../../common/notification/NotificationManager";
import { formatMessage } from "../../../utils/helpers/LanguageHelper";
import {
  CoObjectItem,
  CoObjectSource,
  SheetForm,
  TgkAmountObjectSource,
  TgkDimensionAttributeSource,
  TgkDropDownSource,
  TgkTableObjectSource,
  TgkTextSource,
} from "../../interfaces/CoObject";
import { SourceTypeEnum } from "../../../api/models/source-type-enum";
import { ObjectPanelValidator, TableUpdateStrategy } from "../co-panel/ObjectPanelComponent";
import { FormSheet } from "../../../api/models";

interface ObjectPanelProps {
  coObject: CoObjectItem & { source?: CoObjectSource & { dbApplication?: { id: string } } };
  isEdit: boolean;
  isChecked?: boolean;
  validator: any;
  originalSheetArea?: FormSheet;
  setOriginalSheetArea?: Dispatch<SetStateAction<SheetForm>>;
}

const useObjectPanel = (props) => {
  const coObjectContext = React.useContext(CoObjectContext);
  const username = useAppSelector(selectCpmUsername); // TEMP waiting for real login
  const [state, setState] = React.useState<ObjectPanelProps>({
    isChecked: false,
    coObject: props.coObject,
    isEdit: props.isEdit,
    validator: { isAllValid: false, modifiedFields: false },
    originalSheetArea: props.originalSheetArea,
    setOriginalSheetArea: props.setOriginalSheetArea,
  });
  const { coObject, isEdit, validator, originalSheetArea, setOriginalSheetArea } = state;

  useEffect(() => {
    const isStartingDataValid = validateAll(coObject);
    setState({
      ...state,
      coObject: props.coObject,
      isEdit: props.isEdit,
      validator: {
        isAllValid: isStartingDataValid,
        modifiedFields: false,
      },
    });
    if (!isStartingDataValid && props.isEdit)
      NotificationManager.addNotification({
        title: "",
        message: formatMessage("cof.createnewobject.error.checkfields"),
        type: "warning",
        closable: true,
        removeOnClose: true,
        isTemporary: true,
      });
    return () => {
      LoadingManager.removeAll();
    };
  }, []);

  async function saveCoObject(tableUpdateType: TableUpdateStrategy["typeUpdate"]) {
    if (validateAll(coObject)) {
      LoadingManager.create(SAVE_COOBJECT_NOTIFICATION);
      if (!isEdit) {
        await fetchExecutedFormAndInsertAsync({ coObject: coObject, userId: username });
        setState({
          ...state,
          isEdit: true,
          validator: {
            ...validator,
            modifiedFields: false,
            modifiedSource: false,
          },
        });
      } else {
        const id = await fetchExecutedFormAndEditAsync({
          coObject: coObject,
          userId: username,
          isChanged: validator.modifiedSource,
          tableUpdateType,
        });
        setState({
          ...state,
          coObject: { ...state.coObject, id },
          validator: {
            ...validator,
            modifiedSource: false,
            modifiedFields: false,
          },
        });
      }
      LoadingManager.remove(SAVE_COOBJECT_NOTIFICATION);
      coObjectContext.update({ ...coObject });
    } else {
      NotificationManager.addNotification({
        key: SAVE_COOBJECT_NOTIFICATION,
        message: formatMessage("cof.base.error"),
        title: formatMessage("cof.createnewobject.error.checkfields"),
        type: "error",
        closable: true,
        removeOnClose: true,
        isTemporary: true,
      });
    }
  }

  function validateAll(coObject: CoObjectItem): boolean {
    if (!coObject.info?.objectName || coObject.info.objectName == "") return false;

    if (coObject.info && coObject.info.componentReference) return true;

    if (coObject.info.objectType == "CHECKBOX" || coObject.info.objectType == "FREE_TEXT") return true;
    if (!coObject.source) return false;

    //TABLE VALIDATION
    if (coObject.info.objectType == "TABLE" || coObject.info.objectType == "CHART") {
      const sourceTable = coObject.source as TgkTableObjectSource;
      if (sourceTable.objectSource === SourceTypeEnum.EXCEL) {
        if (
          (sourceTable.area == null && sourceTable.chart == null) ||
          sourceTable.sheet == null ||
          sourceTable.form == null
        )
          return false;
      } else {
        if (
          (sourceTable.area == null && sourceTable.chart == null) ||
          sourceTable.dbApplication == null ||
          sourceTable.sheet == null ||
          sourceTable.form == null
        )
          return false;
      }
    }

    //DROPDOWN VALIDATION
    if (coObject.info.objectType == "DROPDOWN_LIST") {
      const sourceDropDown = coObject.source as TgkDropDownSource;
      if (!sourceDropDown.dropDownList || sourceDropDown.dropDownList.length < 2) return false;
      for (const item of sourceDropDown.dropDownList) {
        if (!item.key || item.key.length === 0 || !item.value || item.value.length === 0) return false;
      }
    }

    //AMOUNT VALIDATION
    if (coObject.info.objectType == "AMOUNT" || coObject.info.objectType == "AMOUNT_AIH") {
      const sourceAmount = coObject.source as TgkAmountObjectSource;
      switch (sourceAmount.objectSource) {
        case SourceTypeEnum.EXCEL:
          if (
            (sourceAmount.area == null && sourceAmount.cell == null) ||
            sourceAmount.sheet == null ||
            sourceAmount.form == null
          )
            return false;
          break;
        case SourceTypeEnum.TAGETIK_FORM:
          if (
            (sourceAmount.area == null && sourceAmount.cell == null) ||
            sourceAmount.dbApplication == null ||
            sourceAmount.sheet == null ||
            sourceAmount.form == null
          )
            return false;
          break;
        default:
          if (sourceAmount.dbApplication == null || !sourceAmount.dimensions?.length) return false;
          for (const item of sourceAmount.dimensions) {
            if (!item.dimensionOid || !item.values?.length) return false;
          }
          break;
      }
    }

    //DIMENSION ATTRIBUTE VALIDATION
    if (coObject.info.objectType == "DIMENSION_ATTRIBUTE") {
      const sourceDimensionAttribute = coObject.source as TgkDimensionAttributeSource;
      if (sourceDimensionAttribute.dbApplication == null || !sourceDimensionAttribute.dimensions?.[0]?.attributeCode)
        return false;
      for (const d of sourceDimensionAttribute.dimensions) {
        if (!d?.dimensionOid || d.values.length !== 1) return false;
      }
    }

    // TGK_TEXT ATTRIBUTE VALIDATION
    if (coObject.info.objectType == "TGK_TEXT") {
      const sourceTgkText = coObject.source as TgkTextSource;
      if (
        sourceTgkText.dbApplication == null ||
        sourceTgkText.analyticalWorkspace == null ||
        sourceTgkText.dataset == null ||
        sourceTgkText.richText == null
      )
        return false;
    }

    return true;
  }

  function updateCoObject(obj: CoObjectItem, validatorObject?: ObjectPanelValidator) {
    setState({
      ...state,
      coObject: obj,
      validator: validatorObject
        ? ({
            isAllValid: validatorObject.isAllValid,
            modifiedFields: state.validator.modifiedFields || validatorObject.modifiedFields,
            modifiedSource: state.validator.modifiedSource || validatorObject.modifiedSource,
          } as ObjectPanelValidator)
        : state.validator,
    });
  }

  return { coObject, isEdit, updateCoObject, saveCoObject, validator, originalSheetArea, setOriginalSheetArea };
};

export default useObjectPanel;
